"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DataModel = void 0;
const Path_1 = require("./Path");
const Errors_1 = require("./Errors");
const utils_1 = require("../utils");
/**
 * Holding the data linked to a given schema
 */
class DataModel {
    /**
     * @param schema node to use as schema for this model
     */
    constructor(schema, options) {
        var _a, _b, _c, _d;
        this.historyMax = (_a = options === null || options === void 0 ? void 0 : options.historyMax) !== null && _a !== void 0 ? _a : 50;
        this.verbose = (_b = options === null || options === void 0 ? void 0 : options.verbose) !== null && _b !== void 0 ? _b : false;
        this.wrapLists = (_c = options === null || options === void 0 ? void 0 : options.wrapLists) !== null && _c !== void 0 ? _c : false;
        this.schema = schema;
        const data = (_d = options === null || options === void 0 ? void 0 : options.initialData) !== null && _d !== void 0 ? _d : schema.default();
        this.data = this.wrapLists ? DataModel.wrapLists(data) : data;
        this.listeners = [];
        this.errors = new Errors_1.Errors();
        this.history = [JSON.stringify(this.data)];
        this.historyIndex = 0;
    }
    /**
     * Adds a listener to notify when the model is invalidated
     * @param listener the listener to be notified
     */
    addListener(listener) {
        this.listeners.push(listener);
    }
    /**
     * Removes a listener from this model
     * @param listener the listener to be removed
     */
    removeListener(listener) {
        const index = this.listeners.indexOf(listener);
        if (index > -1) {
            this.listeners.splice(index, 1);
        }
    }
    /**
     * Updates the history and notifies all
     * listeners that the model is invalidated
     */
    invalidate() {
        const newHistory = JSON.stringify(this.data);
        if (this.history[this.historyIndex] !== newHistory) {
            this.historyIndex += 1;
            this.history.splice(this.historyIndex, this.historyMax, newHistory);
            if (this.history.length > this.historyMax) {
                this.history.splice(0, 1);
                this.historyIndex -= 1;
            }
        }
        this.silentInvalidate();
    }
    /**
     * Notifies all listeners that the model is invalidated
     */
    silentInvalidate() {
        this.listeners.forEach(l => {
            if (l.invalidated)
                l.invalidated(this);
            if (l.errors)
                l.errors(this.errors);
        });
    }
    /**
     * Resets the full data and notifies listeners
     * @param value new model data
     */
    reset(value, loose) {
        this.data = value;
        this.validate(loose);
        this.invalidate();
    }
    /**
     * Gets the data at a specified path
     * @param path path at which to find the data
     * @returns undefined, if the the path does not exist in the data
     */
    get(path) {
        let node = this.data;
        path.forEach(e => {
            if (node === undefined)
                return node;
            if (this.wrapLists && typeof e === 'number') {
                node = node[e].node;
            }
            else {
                node = node[e];
            }
        });
        return node;
    }
    /**
     * Updates the data on a path. Node will be removed when value is undefined
     * @param path path to update
     * @param value new data at the specified path
     */
    set(path, value, silent) {
        if (path.getArray().length === 0) {
            this.reset(value, true);
            return;
        }
        let node = this.data;
        path.pop().forEach(e => {
            if (node[e] === undefined || typeof node[e] === 'string' || typeof node[e] === 'number') {
                if (this.wrapLists && typeof e === 'number') {
                    node[e] = { node: {}, id: (0, utils_1.hexId)() };
                }
                else {
                    node[e] = {};
                }
            }
            if (this.wrapLists && typeof e === 'number') {
                node = node[e].node;
            }
            else {
                node = node[e];
            }
        });
        if (node === null)
            return;
        if (value === undefined || (typeof value === 'number' && isNaN(value))) {
            if (typeof path.last() === 'number') {
                node.splice(path.last(), 1);
            }
            else {
                delete node[path.last()];
            }
        }
        else if (this.wrapLists && typeof path.last() === 'number') {
            node[path.last()] = { node: value, id: (0, utils_1.hexId)() };
        }
        else {
            node[path.last()] = value;
        }
        if (silent)
            return;
        if (this.verbose) {
            console.log('Set', path.toString(), JSON.stringify(value));
        }
        this.validate(true);
        this.invalidate();
    }
    /**
     * Go one step back in history
     */
    undo() {
        if (this.historyIndex > 0) {
            this.historyIndex -= 1;
            this.data = JSON.parse(this.history[this.historyIndex]);
            this.validate(false);
            this.silentInvalidate();
        }
    }
    /**
     * Go one step forward in history.
     */
    redo() {
        if (this.historyIndex < this.history.length - 1) {
            this.historyIndex += 1;
            this.data = JSON.parse(this.history[this.historyIndex]);
            this.validate(false);
            this.silentInvalidate();
        }
    }
    /**
     * Uses the schema to check whether the data is valid
     */
    validate(loose) {
        var _a;
        const path = new Path_1.Path().withModel(this);
        this.errors.clear();
        this.data = (_a = this.schema.validate(path, this.data, this.errors, { loose, wrapLists: this.wrapLists })) !== null && _a !== void 0 ? _a : {};
    }
    error(path, error, ...params) {
        const tempErrors = new Errors_1.Errors();
        tempErrors.add(path, error, params);
        this.listeners.forEach(l => {
            if (l.errors)
                l.errors(tempErrors);
        });
    }
    static wrapLists(value) {
        if (Array.isArray(value)) {
            const res = value.map(v => ({
                node: this.wrapLists(v),
                id: (0, utils_1.hexId)(),
            }));
            for (const a of Object.getOwnPropertySymbols(value)) {
                res[a] = value[a];
            }
            return res;
        }
        else if (typeof value === 'object' && value !== null) {
            const res = {};
            Object.entries(value).map(([k, v]) => {
                res[k] = this.wrapLists(v);
            });
            for (const a of Object.getOwnPropertySymbols(value)) {
                res[a] = value[a];
            }
            return res;
        }
        else {
            return value;
        }
    }
    static unwrapLists(value) {
        if (Array.isArray(value)) {
            const res = value.map(v => this.unwrapLists(v.node));
            for (const a of Object.getOwnPropertySymbols(value)) {
                res[a] = value[a];
            }
            return res;
        }
        else if (typeof value === 'object' && value !== null) {
            const res = {};
            Object.entries(value).map(([k, v]) => {
                res[k] = this.unwrapLists(v);
            });
            for (const a of Object.getOwnPropertySymbols(value)) {
                res[a] = value[a];
            }
            return res;
        }
        else {
            return value;
        }
    }
}
exports.DataModel = DataModel;
